import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { authAPI } from '../../utils/api';

const ProfileImage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshProfile } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const role = searchParams.get('role') || user?.role;
  const userId = searchParams.get('userId') || user?._id || user?.id;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      showError('Please select a JPG or PNG image.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showError('Image must be under 5MB.');
      return;
    }

    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!image) {
      showError('Please select a profile image.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('profileImage', image);

    try {
      const res = await authAPI.uploadProfileImage(formData);
      if (res.data?.success) {
        showSuccess('Profile image uploaded!');
        if (user) {
          const updatedUser = { ...user, profile_image: res.data.data.profile_image };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
        await refreshProfile();
        navigate(`/onboarding/documents?userId=${userId}&role=${role}`);
      } else {
        showError(res.data?.message || 'Failed to upload image');
      }
    } catch (error) {
      showError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSkip = () => {
    navigate(`/onboarding/documents?userId=${userId}&role=${role}`);
  };

  return (
    <div className="max-w-2xl mx-auto py-12 px-4">
      <div className="bg-gray-800 rounded-2xl p-8 shadow-xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gold mb-2">Upload Profile Photo</h1>
          <p className="text-gray-400">
            This photo will be shown to patients when they search for doctors.
          </p>
        </div>

        <div className="flex flex-col items-center mb-8">
          <div
            onClick={() => fileInputRef.current?.click()}
            className="w-40 h-40 rounded-full border-4 border-dashed border-gray-600 flex items-center justify-center cursor-pointer hover:border-gold transition-colors overflow-hidden group"
          >
            {preview ? (
              <img src={preview} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center text-gray-400 group-hover:text-gold transition-colors">
                <svg className="w-12 h-12 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-sm">Tap to select</span>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">JPG or PNG, max 5MB</p>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/jpeg,image/png"
          className="hidden"
        />

        <div className="space-y-3">
          <button
            onClick={handleUpload}
            disabled={uploading || !image}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              uploading || !image
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-gold text-dark shadow-lg shadow-gold/20 hover:scale-[1.02] active:scale-[0.98]'
            }`}
          >
            {uploading ? (
              <div className="flex items-center justify-center space-x-2">
                <svg className="animate-spin h-5 w-5 text-dark" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Uploading...</span>
              </div>
            ) : (
              'Upload & Continue'
            )}
          </button>

          <button
            onClick={handleSkip}
            className="w-full py-3 rounded-xl font-medium text-gray-400 hover:text-white transition-colors border border-gray-600 hover:border-gray-500"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileImage;
